import type { Square, PieceSymbol, Color } from './chessEngine'
import { parseFen } from './chessEngine'
import { PIECE_SYMBOLS } from './chessData'

export type SquareHighlight =
  | 'selected'    // yellow — user-selected piece
  | 'valid-move'  // green dot — where piece can move
  | 'marked'      // blue — user-marked square (Movement mode)
  | 'correct'     // green fill — correct mark
  | 'wrong'       // red fill — wrong mark
  | 'last-move'   // yellow tint — last move
  | 'check'       // red — king in check
  | 'target'      // orange outline — piece to identify
  | 'capture'     // orange dot — capturable pawn

interface ChessBoardProps {
  fen:            string
  size:           number                                   // board px size
  highlights?:    Partial<Record<Square, SquareHighlight>>
  onSquareClick?: (sq: Square) => void
  flipped?:       boolean
  disabled?:      boolean
}

const LIGHT = '#f0d9b5'
const DARK  = '#b58863'

const HIGHLIGHT_STYLES: Record<SquareHighlight, React.CSSProperties> = {
  'selected':   { backgroundColor: 'rgba(255, 215, 0, 0.55)' },
  'last-move':  { backgroundColor: 'rgba(255, 215, 0, 0.35)' },
  'correct':    { backgroundColor: 'rgba(80, 200, 100, 0.55)' },
  'wrong':      { backgroundColor: 'rgba(220, 50, 50, 0.50)' },
  'marked':     { backgroundColor: 'rgba(60, 130, 230, 0.45)' },
  'check':      { backgroundColor: 'rgba(220, 30, 30, 0.55)' },
  'target':     { outline: '3px solid #e67e22', outlineOffset: '-3px' },
  'valid-move': {},   // rendered as dot overlay
  'capture':    {},   // rendered as orange dot overlay
}

const FILES = ['a','b','c','d','e','f','g','h']

export default function ChessBoard({
  fen,
  size,
  highlights = {},
  onSquareClick,
  flipped = false,
  disabled = false,
}: ChessBoardProps) {
  const board    = parseFen(fen)
  const sqSize   = Math.floor(size / 8)
  const fontSize = Math.floor(sqSize * 0.78)

  const files = flipped ? [...FILES].reverse() : FILES
  const ranks = flipped ? [1,2,3,4,5,6,7,8] : [8,7,6,5,4,3,2,1]

  return (
    <div
      role="grid"
      aria-label="Chess board"
      style={{
        display:         'inline-grid',
        gridTemplateColumns: `repeat(8, ${sqSize}px)`,
        gridTemplateRows:    `repeat(8, ${sqSize}px)`,
        border:          '2px solid #8B6914',
        borderRadius:    4,
        overflow:        'hidden',
        userSelect:      'none',
        cursor:          disabled ? 'default' : 'pointer',
        width:           sqSize * 8,
        height:          sqSize * 8,
        flexShrink:      0,
      }}
    >
      {ranks.map((rank) =>
        files.map((file) => {
          const sq        = `${file}${rank}` as Square
          const fileIdx   = FILES.indexOf(file)
          const rankIdx   = rank - 1
          const isLight   = (fileIdx + rankIdx) % 2 !== 0
          const piece     = board[sq]
          const hl        = highlights[sq]
          const isDotMove = hl === 'valid-move' || hl === 'capture'

          return (
            <div
              key={sq}
              role="gridcell"
              aria-label={sq + (piece ? ` ${piece.color === 'w' ? 'white' : 'black'} ${piece.type}` : '')}
              onClick={() => !disabled && onSquareClick?.(sq)}
              style={{
                width:          sqSize,
                height:         sqSize,
                backgroundColor: isLight ? LIGHT : DARK,
                display:        'flex',
                alignItems:     'center',
                justifyContent: 'center',
                position:       'relative',
                ...(hl && !isDotMove ? HIGHLIGHT_STYLES[hl] : {}),
              }}
            >
              {/* Dot overlay for valid move / capture indicators */}
              {isDotMove && (
                <div style={{
                  position:     'absolute',
                  width:        piece ? sqSize * 0.88 : sqSize * 0.32,
                  height:       piece ? sqSize * 0.88 : sqSize * 0.32,
                  borderRadius: '50%',
                  backgroundColor: piece
                    ? (hl === 'capture' ? 'rgba(230,90,0,0.45)' : 'rgba(20,180,60,0.40)')
                    : (hl === 'capture' ? '#e67e22' : '#34a853'),
                  border:       piece ? `3px solid ${hl === 'capture' ? '#e67e22' : '#34a853'}` : 'none',
                  pointerEvents: 'none',
                  zIndex:       1,
                }} />
              )}

              {/* Piece */}
              {piece && (
                <span
                  style={{
                    fontSize,
                    lineHeight:   1,
                    position:     'relative',
                    zIndex:       2,
                    textShadow:   piece.color === 'w'
                      ? '0 1px 2px rgba(0,0,0,0.5)'
                      : '0 1px 3px rgba(255,255,255,0.3)',
                    color:        piece.color === 'w' ? '#fff' : '#1a1a1a',
                  }}
                >
                  {PIECE_SYMBOLS[piece.color as Color][piece.type as PieceSymbol]}
                </span>
              )}
            </div>
          )
        })
      )}
    </div>
  )
}
